import { useCallback, useEffect, useMemo, useRef } from 'react';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import type {
  CellValue,
  EvaluatedCell,
  ScalarSpreadsheetConfigDataInterface,
} from 'models/interfaces/spreadsheet-types/core-types';
import { useCellAnnotationStore } from 'store/useCellAnnotationStore';
import { useCrossReferencePickStore } from 'store/useCrossReferencePickStore';
import type { PickResolution } from 'store/useCrossReferencePickStore';
import type { CellAnnotationPopupSheetProps } from './CellAnnotationPopupSheet.props';
import { PopupPaper } from './CellAnnotationPopupSheet.styles';
import { createCellAnnotationPopupSheet, syncPopupSheetToEntries } from './createCellAnnotationPopupSheet';
import {
  type CellAnnotationSheetColumn,
  type CellAnnotationSheetInitialObject,
  type CellAnnotationSheetTableData,
  ENTRY_ROW_ALIAS_PREFIX,
} from './createCellAnnotationPopupSheet.types';
import type { CellAnnotationRowState } from './types';

type PopupSheet = SpreadsheetConfig<
  CellAnnotationSheetInitialObject,
  CellAnnotationSheetColumn,
  CellAnnotationSheetTableData
>;

// Self-contained popup spreadsheet for one annotated cell.
//
// The popup builds and owns its own SpreadsheetConfig + useWorkbook so that
// auto-grow (adding a fresh trailing row when the user fills the previous
// trailing) is local to the popup. The host sheet is intentionally NOT in
// this workbook — it would otherwise have to be re-evaluated on every
// auto-grow tick, which churned the host's datasheet state and looked like
// the popup was stalling/closing.
//
// Save flow still works without host integration: reverseParser writes edits
// to the cell-annotation store, and `distributeDirtyPayload` reads from there
// at save time.
const CellAnnotationPopupSheet: React.FC<CellAnnotationPopupSheetProps> = ({ cellKey, config, inheritedFormat }) => {
  // Watch the entry count for THIS cellKey so we can extend the rowConfig in
  // place when the user fills the trailing row. Counts only non-placeholder
  // rows — buildBlankState seeds an `isPlaceholder: true` row for multi_row
  // popups that should not contribute.
  const entryCount = useCellAnnotationStore(s => (s.states[cellKey]?.rows ?? []).filter(r => !r.isPlaceholder).length);

  // Build the SpreadsheetConfig once and keep the same instance across
  // re-renders. Mutating the same instance (via syncPopupSheetToEntries +
  // local re-eval) is what lets ScalarDatasheet preserve its internal state
  // through an auto-grow tick.
  const sheetRef = useRef<PopupSheet | null>(null);
  if (!sheetRef.current) {
    const initialRows = useCellAnnotationStore.getState().states[cellKey]?.rows ?? [];
    sheetRef.current = createCellAnnotationPopupSheet({
      cellKey,
      initialRows,
      config,
      inheritedFormat,
      parentWidth: 0,
    });
  }

  // Stable single-element array so useWorkbook's effect doesn't rebuild every
  // render. We never swap the sheet instance; auto-grow mutates it in place.
  const spreadsheets = useMemo(
    () => [sheetRef.current as PopupSheet] as unknown as ScalarSpreadsheetConfigDataInterface[],
    []
  );
  const { workbook, onChange, evaluateSpreadsheets } = useWorkbook(spreadsheets);

  const buildOnPicked = useCallback(
    (rowPosition: number) =>
      ({ value, mappingReferences }: PickResolution) => {
        const store = useCellAnnotationStore.getState();
        const currentRows = store.states[cellKey]?.rows ?? [];

        const numericValue: number | null = (() => {
          if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
          }
          if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed === '') {
              return null;
            }
            const num = Number(trimmed);
            return Number.isFinite(num) ? num : null;
          }
          return null;
        })();

        const existingIndex = currentRows.findIndex(r => r.position === rowPosition);
        let nextRows: CellAnnotationRowState[];
        if (existingIndex >= 0) {
          nextRows = currentRows.map((r, i) =>
            i === existingIndex
              ? {
                ...r,
                value: numericValue,
                metadata: { ...r.metadata, mappingReferences },
                isPlaceholder: false,
              } : r
          );
        } else {
          nextRows = [
            ...currentRows.filter(r => !r.isPlaceholder),
            {
              position: rowPosition,
              value: numericValue,
              metadata: { mappingReferences },
              isPlaceholder: false,
            },
          ];
        }

        store.setRows(cellKey, nextRows);
      },
    [cellKey]
  );

  // Wrap onChange so typing `=` in a value cell registers a pick target.
  // Typing anything else cancels any pending target for that row (the user
  // changed their mind).
  const wrappedOnChange = useCallback(
    (cell: EvaluatedCell, expr: CellValue, customFn?: (changes: EvaluatedCell[]) => void) => {
      const isValueColumn = cell?.columnLegend === 'A';
      const rowAlias = typeof cell?.alias === 'string' ? cell.alias : '';
      const rowPosition = rowAlias.startsWith(ENTRY_ROW_ALIAS_PREFIX)
        ? Number(rowAlias.slice(ENTRY_ROW_ALIAS_PREFIX.length))
        : NaN;

      if (isValueColumn && Number.isFinite(rowPosition)) {
        const targetId = `${cellKey}:${rowPosition}`;
        const pickStore = useCrossReferencePickStore.getState();

        if (expr === '=') {
          pickStore.setTarget({
            targetId,
            onPicked: buildOnPicked(rowPosition),
          });
        } else if (pickStore.activeTarget?.targetId === targetId) {
          // User changed their mind — typed something other than `=` into the
          // same cell that was waiting for a pick. Cancel cleanly.
          pickStore.cancel();
        }
      }

      return onChange(cell, expr, customFn);
    },
    [onChange, cellKey, buildOnPicked]
  );

  // When the user fills the trailing row, reverseParser bumps the store's
  // entry count. Mirror that into the live SpreadsheetConfig and re-evaluate
  // the popup's local workbook so a fresh trailing row is rendered. Because
  // the SpreadsheetConfig identity is preserved, ScalarDatasheet keeps its
  // selection / editing / focus state across this update.
  //
  // We skip the very first run (initial render): the SpreadsheetConfig was
  // already constructed from the same store state, and useWorkbook's mount
  // effect will evaluate it.
  const didInitialEvalRef = useRef(false);
  useEffect(() => {
    if (!didInitialEvalRef.current) {
      didInitialEvalRef.current = true;
      return;
    }
    const sheet = sheetRef.current;
    if (!sheet) {
      return;
    }
    const entries = (useCellAnnotationStore.getState().states[cellKey]?.rows ?? []).filter(r => !r.isPlaceholder);
    syncPopupSheetToEntries(sheet, entries, config);
    // Local re-eval: only this popup's sheet is in this workbook, so no host
    // sheet is touched.
    evaluateSpreadsheets([sheet] as unknown as ScalarSpreadsheetConfigDataInterface[]);
  }, [entryCount, cellKey, config, evaluateSpreadsheets]);

  const sheetFromWorkbook =
    (workbook as Record<string, PopupSheet> | undefined)?.[`cell_ann_${cellKey}`] ?? sheetRef.current;
  const spreadsheetProps = useMemo(() => {
    if (!sheetFromWorkbook) {
      return null;
    }
    return {
      ...sheetFromWorkbook,
      data: sheetFromWorkbook.data ?? [],
      onChange: wrappedOnChange,
      sheet: sheetFromWorkbook,
      workbook,
    };
  }, [sheetFromWorkbook, wrappedOnChange, workbook]);

  if (!spreadsheetProps) {
    return null;
  }

  // Stop keyboard propagation: events from the popup's input would otherwise
  // bubble up to the host sheet's data-grid-container, where react-datasheet's
  // handleKey calls preventDefault on Backspace and arrow keys, blocking the
  // input's native cursor movement and delete behavior.
  const handlePopupKeyDown = (event: React.KeyboardEvent) => {
    event.stopPropagation();
    if (event.key !== '=') {
      return;
    }
    const inputEl = event.target as HTMLElement | null;
    if (!inputEl || (inputEl.tagName !== 'INPUT' && inputEl.tagName !== 'TEXTAREA')) {
      return;
    }
    const idMatch = inputEl.id.match(new RegExp(`${ENTRY_ROW_ALIAS_PREFIX}(\\d+)-A\\d+_editor$`));
    if (!idMatch) {
      return;
    }
    const rowPosition = Number(idMatch[1]);
    if (!Number.isFinite(rowPosition)) {
      return;
    }
    useCrossReferencePickStore.getState().setTarget({
      targetId: `${cellKey}:${rowPosition}`,
      onPicked: buildOnPicked(rowPosition),
    });
    // Defer so the input's own onChange runs first and DataCell.state.value
    // is `=` by the time pageClick commits. 25ms mirrors Cell.tsx's timing.
    setTimeout(() => {
      document.body.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true, view: window }));
    }, 25);
  };
  const stopKeyboardPropagation = (event: React.KeyboardEvent) => {
    event.stopPropagation();
  };

  return (
    <PopupPaper
      elevation={4}
      data-testid={`cell-annotation-popup-${cellKey}`}
      onKeyDown={handlePopupKeyDown}
      onKeyUp={stopKeyboardPropagation}>
      {/* The spread carries our minimal popup shape; the legacy
          ScalarSpreadsheetProps type insists on every config field, but the
          host's generic onChange + sheet are what actually drive rendering.
          Cast to any to mirror how other call sites adapt the legacy typing. */}
      <ScalarSpreadsheet {...(spreadsheetProps as any)} />
    </PopupPaper>
  );
};

export default CellAnnotationPopupSheet;
